import { getData, logout } from "@/features/midtrans/actions";
import { columns, type Payment } from "@/features/midtrans/column";
import { DataTable } from "@/features/midtrans/data-table";
import { DestinationsTable } from "@/features/webhook-destinations/data-table";
import React, { useEffect, useState } from "react";
import { Activity, Webhook, Menu, X, CheckCircle2, AlertTriangle, LogOut } from "lucide-react";

type Tab = "notifications" | "destinations";
type Mode = "production" | "sandbox";

export default function Midtrans() {
  const [activeTab, setActiveTab] = useState<Tab>("notifications");
  const [mode, setMode] = useState<Mode>("production");
  const [data, setData] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const fetchNotifications = async (m: Mode) => {
    setLoading(true);
    const res: Payment[] = await getData(m);
    setData(res ?? []);
    setLoading(false);
  };

  useEffect(() => {
    if (activeTab === "notifications") {
      fetchNotifications(mode);
    }
  }, [activeTab, mode]);

  const isProd = mode === "production";

  return (
    <div className="flex flex-col md:flex-row h-screen overflow-hidden">
      
      {/* HEADER FOR MOBILE ONLY */}
      <div className="md:hidden flex items-center justify-between p-4 border-b border-border bg-surface z-30 shrink-0">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">
          Forwarder
        </h1>
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 border border-border rounded-md bg-surface hover:bg-surface-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* SIDEBAR */}
      <aside className={`
        ${isMobileMenuOpen ? 'flex' : 'hidden'} 
        md:flex w-full md:w-64 border-b md:border-b-0 md:border-r border-border bg-surface flex-col justify-between z-20 shrink-0 absolute md:relative top-[65px] md:top-0 h-[calc(100vh-65px)] md:h-full
      `}>
        <div>
          {/* Header (Desktop Only) */}
          <div className="hidden md:flex flex-col p-6 border-b border-border">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">Forwarder</h1>
            <p className="text-sm text-muted mt-1">Midtrans Webhook Router</p>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-2">
            <button
              onClick={() => { setActiveTab("notifications"); setIsMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                activeTab === "notifications"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-foreground hover:bg-surface-hover"
              }`}
            >
              <Activity className="w-4 h-4" />
              Payload Stream
            </button>
            <button
              onClick={() => { setActiveTab("destinations"); setIsMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                activeTab === "destinations"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-foreground hover:bg-surface-hover"
              }`}
            >
              <Webhook className="w-4 h-4" />
              Endpoints
            </button>
            <button
              onClick={async () => {
                await logout();
                window.location.reload();
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all text-destructive hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring mt-4"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </nav>
        </div>

        {/* Tactile Mode Switcher */}
        <div className="p-4 border-t border-border mt-auto bg-surface">
          <label className="text-xs font-semibold text-muted uppercase tracking-wider mb-3 block">Environment</label>
          <div className="p-1 rounded-lg border border-border bg-background flex relative overflow-hidden shadow-inner">
            <div 
              className="absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-md transition-all duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] shadow-sm border border-border/50"
              style={{
                backgroundColor: 'var(--surface)',
                transform: isProd ? 'translateX(0)' : 'translateX(100%)',
                left: isProd ? '4px' : '0px'
              }}
            />
            
            <button
              onClick={() => setMode("production")}
              className={`flex-1 py-2.5 text-sm font-medium relative z-10 transition-colors focus-visible:outline-none focus-visible:ring-inset focus-visible:ring-2 focus-visible:ring-ring rounded-md flex items-center justify-center gap-2 ${
                isProd ? "text-prod" : "text-muted hover:text-foreground"
              }`}
            >
              {isProd && <div className="w-2 h-2 rounded-full bg-prod shadow-[0_0_8px_rgba(60,110,113,0.5)]"></div>}
              Production
            </button>
            <button
              onClick={() => setMode("sandbox")}
              className={`flex-1 py-2.5 text-sm font-medium relative z-10 transition-colors focus-visible:outline-none focus-visible:ring-inset focus-visible:ring-2 focus-visible:ring-ring rounded-md flex items-center justify-center gap-2 ${
                !isProd ? "text-sandbox" : "text-muted hover:text-foreground"
              }`}
            >
              {!isProd && <div className="w-2 h-2 rounded-full bg-sandbox shadow-[0_0_8px_rgba(217,108,74,0.5)]"></div>}
              Sandbox
            </button>
          </div>
          <div className="mt-4 flex items-start gap-2 px-1">
            {isProd ? (
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-prod" />
            ) : (
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-sandbox" />
            )}
            <p className="text-xs text-muted leading-tight">
              {isProd ? "Routing live transaction payloads." : "Routing simulated test payloads."}
            </p>
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 flex flex-col relative overflow-hidden w-full bg-background">
        
        {/* Top header area */}
        <header className="h-16 shrink-0 border-b border-border flex items-center justify-between px-4 md:px-8 relative z-10 bg-surface">
          <h2 className="text-lg font-semibold tracking-tight truncate mr-2 text-foreground">
            {activeTab === "notifications" ? "Payload Stream" : "Endpoint Configuration"}
          </h2>
          <div className="flex items-center gap-2 text-sm shrink-0">
            <span className="text-muted hidden sm:inline">Status</span>
            <span className="flex items-center gap-2 font-medium" style={{ color: isProd ? 'var(--prod)' : 'var(--sandbox)' }}>
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-40" style={{ backgroundColor: 'currentColor' }}></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: 'currentColor' }}></span>
              </span>
              Active
            </span>
          </div>
        </header>

        {/* Content scroll area */}
        <div className="flex-1 overflow-auto p-4 md:p-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            {activeTab === "notifications" && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <p className="text-sm text-muted hidden sm:block">Monitor incoming webhooks from the Midtrans API.</p>
                  <button 
                    onClick={() => fetchNotifications(mode)}
                    disabled={loading}
                    className="text-sm px-4 py-2 min-h-[44px] rounded-md border border-border bg-surface hover:bg-surface-hover transition-colors text-foreground flex items-center justify-center sm:justify-start gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed shadow-sm w-full sm:w-auto font-medium"
                  >
                    {loading ? <Activity className="w-4 h-4 animate-spin text-muted" /> : <Activity className="w-4 h-4 text-muted" />}
                    {loading ? "Fetching..." : "Refresh Stream"}
                  </button>
                </div>

                <div className="border border-border bg-surface rounded-lg shadow-sm p-4 relative min-h-[400px]">
                  {loading && data.length === 0 && (
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-surface/80 backdrop-blur-sm rounded-lg border border-border">
                      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mb-4" style={{ borderColor: 'var(--muted)', borderTopColor: 'transparent' }}></div>
                      <span className="text-sm text-muted font-medium">Fetching Data...</span>
                    </div>
                  )}
                  {loading && data.length > 0 && (
                    <div className="absolute top-0 right-0 m-4 z-20 flex items-center gap-2 bg-surface border border-border px-3 py-1.5 rounded-full shadow-sm">
                      <div className="w-3 h-3 border-[1.5px] border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--muted)', borderTopColor: 'transparent' }}></div>
                      <span className="text-xs font-medium text-muted">Updating</span>
                    </div>
                  )}
                  
                  <div className={loading && data.length === 0 ? "opacity-0" : "opacity-100 transition-opacity duration-300"}>
                    <DataTable
                      columns={columns}
                      data={data}
                      setData={setData}
                      onRefresh={() => fetchNotifications(mode)}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "destinations" && (
              <div className="space-y-6">
                <p className="text-sm text-muted">Configure downstream targets for intercepted payloads.</p>
                <div className="border border-border bg-surface rounded-lg shadow-sm p-4">
                  <DestinationsTable />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
